import React, { useEffect, useState } from "react";
import "./Scheduling.css";
import { SchedulingMetricsModal } from "./SchedulingMetricsModal";
import axios from "axios";
import { useAuthFetch } from "../hooks/useAuthFetch";

// Move this function to the top level, before the Scheduling component:
export const formatUSDate = (dateString) => {
  if (!dateString) return '';

  // Handle ISO string like '2025-04-16T00:00:00.000Z'
  if (dateString.includes('T')) {
    const [datePart] = dateString.split('T'); // '2025-04-16'
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
  }

  // Handle format like '2025-04-16'
  if (dateString.includes('-')) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }

  return dateString; // Return as is if format is not recognized
};

function Scheduling({ projectData }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  // ----- State for date items (First, Most Recent, Next Onsite) -----
  const [dateItems, setDateItems] = useState([
    { label: "First Onsite", date: "N/A", source: "miter" },
    { label: "Most Recent Onsite", date: "N/A", source: "miter" },
    { label: "Next Onsite", date: "N/A", source: "outlook" },
  ]);

  // ----- State for scheduling metrics (kept for event handling, but not displayed) -----
  const [metrics, setMetrics] = useState([
    { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
    { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
    { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
  ]);

  // ----- Modal State -----
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    metricType: null,
    currentValues: null,
  });

  const [loading, setLoading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarId, setCalendarId] = useState(null);
  
  // ----- Calendar Picker State -----
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 1) Fetch Scheduling Metrics (kept for event handling)
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchSchedulingMetrics = async () => {
    if (!projectData?.opportunityNo || !isReady) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules/${projectData.opportunityNo}`,
        await getAuthConfig()
      );

      if (response.data.status === "success" && response.data.data) {
        const data = response.data.data;
        setMetrics([
          {
            label: "Mobs Used/Contracted",
            value: `${data.mobs_used}/${data.mobs_contracted}`,
            type: "mobs",
            used: data.mobs_used,
            total: data.mobs_contracted,
          },
          {
            label: "Days Used/Estimated",
            value: `${data.days_used}/${data.days_estimated}`,
            type: "days",
            used: data.days_used,
            total: data.days_estimated,
          },
          {
            label: "Trips Used/Estimated",
            value: `${data.trip_used}/${data.trip_estimated}`,
            type: "trips",
            used: data.trip_used,
            total: data.trip_estimated,
          },
        ]);
      } else {
        setMetrics([
          { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
          { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
          { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
        ]);
      }
    } catch (error) {
      console.error("Error fetching scheduling metrics:", error);
      setMetrics([
        { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
        { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
        { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
      ]);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) Fetch Onsite Dates
  //    First + Most Recent → Miter timesheets
  //    Next Onsite → Outlook calendar opportunity events (unchanged)
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchOnsiteDatesFromAPI = async () => {
    if (!isReady) return;
    if (!projectData?.opportunityNo && !projectData?.projectNo) return;

    setLoading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const authConfig = await getAuthConfig();

      const outlookPromise = projectData?.opportunityNo
        ? axios.get(
            `${base}/api/opportunity-events/${projectData.opportunityNo}`,
            authConfig
          )
        : Promise.resolve(null);

      const miterPromise = projectData?.projectNo
        ? axios.get(
            `${base}/api/miter/production-performance/${encodeURIComponent(projectData.projectNo)}/onsite-dates`,
            authConfig
          )
        : Promise.resolve(null);

      const [outlookResult, miterResult] = await Promise.allSettled([
        outlookPromise,
        miterPromise,
      ]);

      let nextOnsite = "N/A";
      if (outlookResult.status === "fulfilled" && outlookResult.value?.data) {
        const eventData = outlookResult.value.data;
        nextOnsite = eventData.next_onsite
          ? formatUSDate(eventData.next_onsite)
          : "N/A";
      } else if (outlookResult.status === "rejected") {
        console.error("Error fetching Next Onsite from opportunity events:", outlookResult.reason);
      }

      let firstOnsite = "N/A";
      let mostRecentOnsite = "N/A";
      if (miterResult.status === "fulfilled" && miterResult.value?.data?.status === "success") {
        const miterData = miterResult.value.data.data || {};
        firstOnsite = miterData.first_onsite
          ? formatUSDate(miterData.first_onsite)
          : "N/A";
        mostRecentOnsite = miterData.most_recent_onsite
          ? formatUSDate(miterData.most_recent_onsite)
          : "N/A";
      } else if (miterResult.status === "rejected") {
        console.error("Error fetching First/Most Recent Onsite from Miter:", miterResult.reason);
      }

      setDateItems([
        { label: "First Onsite", date: firstOnsite, source: "miter" },
        { label: "Most Recent Onsite", date: mostRecentOnsite, source: "miter" },
        { label: "Next Onsite", date: nextOnsite, source: "outlook" },
      ]);
    } catch (error) {
      console.error("Error fetching onsite dates:", error);
      setDateItems([
        { label: "First Onsite", date: "N/A", source: "miter" },
        { label: "Most Recent Onsite", date: "N/A", source: "miter" },
        { label: "Next Onsite", date: "N/A", source: "outlook" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3) Fetch Calendar Data for Initial Start Date
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchCalendarData = async () => {
    if (!projectData?.projectNo || !isReady) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/calendars/project/${projectData.projectNo}`,
        await getAuthConfig()
      );

      if (response.data && response.data.status === "success" && response.data.data && response.data.data.length > 0) {
        const calendarRecord = response.data.data[0];
        const calendarDateStr = calendarRecord.calendar_date;
        
        // Store calendar data for the icon
        setCalendarDate(formatUSDate(calendarDateStr));
        setCalendarId(calendarRecord.id);
      } else {
        // No calendar data found
        setCalendarDate(null);
        setCalendarId(null);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      setCalendarDate(null);
      setCalendarId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) Effects: Fetch Metrics, Onsite Dates, and Calendar Data
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchSchedulingMetrics();
    fetchOnsiteDatesFromAPI();
    fetchCalendarData();
  }, [projectData, isReady, getAuthConfig]);
  
  // Listen for metrics updates from other components
  useEffect(() => {
    const handleMetricsUpdate = (event) => {
      if (event.detail) {
        setMetrics(event.detail);
      }
    };
    
    window.addEventListener('metricsUpdated', handleMetricsUpdate);
    
    return () => {
      window.removeEventListener('metricsUpdated', handleMetricsUpdate);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDateClick = (item) => {
    // Only Next Onsite comes from Outlook calendar events
    if (item.source === "outlook" && item.date !== "N/A") {
      window.location.href = 'outlook:calendar';
    }
  };

  const handleMetricClick = (metric) => {
    setModalConfig({
      isOpen: true,
      metricType: metric.type,
      currentValues: { used: metric.used, total: metric.total },
    });
  };

  const closeModal = (refreshData = false) => {
    setModalConfig({
      isOpen: false,
      metricType: null,
      currentValues: null,
    });
    
    // If data was updated in the modal, refresh metrics
    if (refreshData) {
      // Instead of calling local fetch function, trigger global refresh for the parent component to handle
      window.dispatchEvent(new CustomEvent('refreshMetrics'));
    }
  };

  // ----- Calendar Operations -----
  const handleCalendarClick = () => {
    // Set the temp date to existing date if editing, or empty if adding
    if (calendarDate) {
      // Convert MM/DD/YYYY to YYYY-MM-DD for the input
      const [month, day, year] = calendarDate.split('/');
      setTempSelectedDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    } else {
      setTempSelectedDate('');
    }
    setShowCalendarPicker(true);
  };

  const handleCalendarSave = async () => {
    if (!tempSelectedDate) return;

    if (!projectData?.projectNo) {
      alert('Project data is missing');
      return;
    }

    try {
      // Convert MM/DD/YYYY format to YYYY-MM-DD if needed
      let dateForAPI = tempSelectedDate;
      if (tempSelectedDate.includes('/')) {
        const [month, day, year] = tempSelectedDate.split('/');
        dateForAPI = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Use PUT for updates (need calendar ID), POST for new entries
      if (calendarDate && calendarId) {
        // Update existing calendar
        const payload = {
          calendar_date: dateForAPI
        };
        
        await axios.put(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/calendars/${calendarId}`,
          payload,
          await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
        );
      } else {
        // Create new calendar
        const payload = {
          project_number: projectData.projectNo,
          calendar_date: dateForAPI
        };
        
        await axios.post(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/calendars`,
          payload,
          await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
        );
      }

      // Close picker and refresh data
      setShowCalendarPicker(false);
      fetchCalendarData();
    } catch (error) {
      console.error("Error updating calendar date:", error);
      alert('Failed to update calendar date. Please try again.');
    }
  };

  const handleCalendarCancel = () => {
    setShowCalendarPicker(false);
    setTempSelectedDate('');
  };

  const handleCalendarDelete = async () => {
    if (!calendarId || !calendarDate) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/calendars/${calendarId}`,
        await getAuthConfig()
      );
      
      // Refresh the calendar data after deletion
      setCalendarDate(null);
      setCalendarId(null);
      fetchCalendarData();
    } catch (error) {
      console.error("Error deleting calendar date:", error);
    }
  };

  // Helper function to format date as MM/DD/YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to convert date to yyyy-mm-dd format for input
  const formatForDateInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // Helper function to handle date change
  const handleDateChange = (value) => {
    if (!value) {
      setTempSelectedDate('');
      return;
    }
    setTempSelectedDate(value);
  };


  // ─────────────────────────────────────────────────────────────────────────────
  // 5) Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <section className="scheduling">
      {/* Modal components */}
      {modalConfig.isOpen && (
        <SchedulingMetricsModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          metricType={modalConfig.metricType}
          currentValues={modalConfig.currentValues}
          projectData={projectData}
        />
      )}

      
        <div className="scheduling-header max-md:flex-col max-md:items-start">
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <h2 className="scheduling-title">Scheduling</h2>
            {loading && (
              <div className="loader" style={{ marginBottom: "10px" }} />
            )}
          </div>
          
          {/* Calendar Display with Operations */}
          <div className="calendar-section max-md:w-full max-md:mt-3">
            {/* Calendar Picker (shown above when active) */}
            {showCalendarPicker && (
              <div className="calendar-picker-container">
                <div className="calendar-picker-content">
                  <div className="calendar-picker-header">
                    <span className="calendar-picker-title">
                      {calendarDate ? 'Edit Date' : 'Select Date'}
                    </span>
                    {tempSelectedDate && (
                      <span className="calendar-selected-date">
                        Selected: {formatUSDate(tempSelectedDate)}
                      </span>
                    )}
                  </div>
                  
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      value={tempSelectedDate ? formatDateForDisplay(tempSelectedDate) : ''}
                      placeholder="MM/DD/YYYY"
                      readOnly
                      className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500 cursor-pointer calendar-date-input"
                    />
                    <input
                      type="date"
                      value={formatForDateInput(tempSelectedDate)}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="absolute right-0 top-0 h-full px-2 py-1 opacity-0 cursor-pointer"
                      style={{ width: '3rem' }}
                    />
                    <button 
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                      aria-label="Calendar"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="calendar-picker-actions">
                    <button
                      onClick={handleCalendarSave}
                      className="calendar-save-btn"
                      disabled={!tempSelectedDate}
                      title="Save Date"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleCalendarCancel}
                      className="calendar-cancel-btn"
                      title="Cancel"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Initial Start Date and Calendar in same line */}
            <div className="initial-start-date-row flex items-center gap-2">
              <span className="initial-start-date-label text-sm text-gray-700 font-medium">Initial Start Date:</span>
              <div className="calendar-icon-container initial-start-date-control flex items-center gap-2">
                {calendarDate ? (
                  // Show date with edit/delete buttons when date exists
                  <>
                    <span className="initial-start-date-value text-sm text-gray-700">{calendarDate}</span>
                    <button
                      onClick={handleCalendarClick}
                      className="calendar-edit-btn"
                      title="Edit Calendar Date"
                    >
                      <svg className="w-4 h-4 text-blue-600 hover:text-blue-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={handleCalendarDelete}
                      className="calendar-delete-btn"
                      title="Delete Calendar Date"
                    >
                      <svg className="w-4 h-4 text-red-600 hover:text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </>
                ) : (
                  // Show "Select" text with calendar icon when no date exists
                  <>
                    <span className="initial-start-date-value initial-start-date-value--empty text-sm text-gray-400">Select</span>
                    <button
                      onClick={handleCalendarClick}
                      className="calendar-icon-btn"
                      title="Add Calendar Date"
                    >
                      <svg 
                        className="text-gray-600 hover:text-red-700 transition-colors" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Dates Grid */}
      <div className="schedule-grid">
        {dateItems.map((item, index) => (
          <div
            key={index}
            className="schedule-item"
            onClick={() => handleDateClick(item)}
            style={{
              cursor:
                item.source === "outlook" && item.date !== "N/A"
                  ? "pointer"
                  : "default",
            }}
          >
            <div className="item-label">{item.label}</div>
            <div className="item-value">{item.date}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Scheduling;
